"use client";

import { Component, type ReactNode } from "react";

/**
 * Falls back to the standard chrome when the applied header or footer throws
 * while rendering (feature: header-footer-builder).
 *
 * AC-03's promise is "fall back to the standard header/footer", and until this
 * existed that was implemented for exactly one failure mode — a fetch that
 * yields no document. Every render-time failure took the whole page down
 * instead: the storefront's own navigation is inside the subtree that threw.
 *
 * ## What it does and does not catch
 *
 * A React error boundary is a CLIENT boundary. It catches a throw during client
 * render or hydration of the subtree — a slot that is missing at hydration, an
 * icon import regression, an element body that trips on a shape the schema
 * admits. It does NOT catch a throw during the server render: React Server
 * Components have no server-side equivalent, and such a throw propagates to the
 * route's `error.tsx` as it did before. Do not read this as full coverage.
 *
 * `children` and `fallback` are both passed as props, so the server components
 * inside them stay server-rendered — this adds the class and nothing else to
 * the client bundle.
 */
export class AppliedChromeBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
